import { useRef, useState, type ChangeEvent } from 'react';
import { Box, Button, Dialog, IconButton, Menu, MenuItem, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { encodeBackup, parseBackup, type Backup } from './backup';

/**
 * Saved's menu, at the right of its results bar: the calendar file, the
 * backup and its restore, and Clear Saved. Everything here works on the
 * browser's own lists; nothing leaves the device except as a file the user saves.
 */

const Trigger = styled('button', { shouldForwardProp: (p) => p !== '$round' })<{ $round: boolean }>(
  ({ $round }) => ({
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 34,
    minWidth: 34,
    padding: $round ? 0 : '0 14px 0 12px',
    borderRadius: 999,
    border: '1px solid rgba(243,238,231,0.35)',
    background: 'transparent',
    color: BRAND.parchment,
    fontFamily: FONTS.body,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.08)' },
    '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2 },
  }),
);

/** A calendar page with an arrow out of it: take Saved elsewhere. */
function ExportGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5M4 10h7M8 3v4M15 3h6v6M21 3l-8 8"
      />
    </svg>
  );
}

/** Three dots: the dialog's overflow. */
function MoreGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {[5, 12, 19].map((x) => (
        <circle key={x} cx={x} cy={12} r={1.9} fill="currentColor" />
      ))}
    </svg>
  );
}

const menuPaper = {
  background: BRAND.mochaDeep,
  backgroundImage: 'none',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.14)',
  borderRadius: 2,
  minWidth: 220,
  '& .MuiMenuItem-root': { fontFamily: FONTS.body, fontSize: 14, py: 1.1, gap: 1.2 },
  '& .MuiMenuItem-root.Mui-disabled': { opacity: 0.4 },
} as const;

const dialogPaper = { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 3 } as const;

const primarySx = {
  background: BRAND.parchment,
  color: BRAND.mocha,
  fontWeight: 700,
  borderRadius: 999,
  px: 3,
  '&:hover': { background: '#fff' },
  '&.Mui-disabled': { background: 'rgba(243,238,231,0.25)', color: 'rgba(54,47,42,0.5)' },
} as const;

const quietSx = { color: 'rgba(243,238,231,0.75)', fontWeight: 600, borderRadius: 999, px: 2 } as const;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: BRAND.parchment,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
    '& fieldset': { borderColor: 'rgba(243,238,231,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(243,238,231,0.5)' },
    '&.Mui-focused fieldset': { borderColor: BRAND.parchment },
  },
} as const;

const Panel = styled('div')({ position: 'relative', padding: '26px 26px 22px', color: BRAND.parchment });

function DialogTitle({ children }: { children: string }) {
  return (
    <Typography sx={{ fontFamily: FONTS.serif, fontWeight: 700, fontSize: 24, mb: 0.8, pr: 5 }}>{children}</Typography>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <IconButton
      onClick={onClose}
      aria-label="Close"
      sx={{ position: 'absolute', top: 12, right: 14, color: BRAND.parchment }}
    >
      ✕
    </IconButton>
  );
}

interface Props {
  /** Going and Maybe, in this city. */
  count: number;
  cityLabel: string;
  /** The label beside the icon; a phone has room for the icon only. */
  round: boolean;
  /**
   * The Share my calendar dialog's overflow (contract §18): a round "…" with
   * Back up, Restore and Clear, since the dialog has its own Download .ics.
   */
  overflow?: boolean;
  makeBackup: () => Backup;
  onDownloadIcs: () => void;
  onDownloadBackup: (backup: Backup) => void;
  /** Merges a parsed backup; the caller says how it went. */
  onRestore: (backup: Backup) => void;
  onClear: () => void;
  onCopied: (ok: boolean) => void;
}

type Open = 'backup' | 'restore' | 'clear' | null;

export default function ScheduleMenu({
  count,
  cityLabel,
  round,
  overflow = false,
  makeBackup,
  onDownloadIcs,
  onDownloadBackup,
  onRestore,
  onClear,
  onCopied,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState<Open>(null);
  /** Taken when the dialog opens, so the string on screen is the one downloaded. */
  const [backup, setBackup] = useState<Backup | null>(null);
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement | null>(null);

  const choose = (what: Open) => {
    setAnchor(null);
    if (what === 'backup') setBackup(makeBackup());
    if (what === 'restore') {
      setPasted('');
      setError(null);
    }
    setOpen(what);
  };
  const close = () => setOpen(null);

  const restore = (text: string) => {
    const parsed = parseBackup(text);
    if (!parsed) {
      setError("That isn't a CommonGround backup. Paste the whole line, starting cg1:, or choose the .json file.");
      return;
    }
    onRestore(parsed);
    close();
  };

  const readFile = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = '';
    if (!chosen) return;
    chosen.text().then(restore, () => setError("That file couldn't be read."));
  };

  const code = backup ? encodeBackup(backup) : '';

  return (
    <>
      <Trigger
        type="button"
        $round={round || overflow}
        aria-label={overflow ? 'More: back up, restore, clear' : 'Export and back up'}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {overflow ? <MoreGlyph /> : <ExportGlyph />}
        {!round && !overflow && 'Export & back up'}
      </Trigger>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { ...menuPaper, mt: 0.8 } } }}
      >
        {!overflow && (
          <MenuItem
            disabled={!count}
            onClick={() => {
              setAnchor(null);
              onDownloadIcs();
            }}
          >
            Download .ics
          </MenuItem>
        )}
        <MenuItem onClick={() => choose('backup')}>Back up…</MenuItem>
        <MenuItem onClick={() => choose('restore')}>Restore…</MenuItem>
        <MenuItem disabled={!count} onClick={() => choose('clear')} sx={{ color: '#ffb9b9' }}>
          Clear Saved…
        </MenuItem>
      </Menu>

      <Dialog open={open === 'backup'} onClose={close} maxWidth="sm" fullWidth slotProps={{ paper: { sx: dialogPaper } }}>
        <Panel>
          <CloseButton onClose={close} />
          <DialogTitle>Back up Saved</DialogTitle>
          <Typography sx={{ fontSize: 14, opacity: 0.75, mb: 2.2, lineHeight: 1.5 }}>
            Your Going and Maybe lists live only in this browser. Save the file, or copy the line below somewhere safe,
            and restore it here or on another device. {cityLabel} only.
          </Typography>
          <TextField
            value={code}
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            slotProps={{ htmlInput: { readOnly: true, 'aria-label': 'Backup code', spellCheck: false } }}
            onFocus={(e) => e.target.select()}
            sx={{ mb: 2, ...fieldSx }}
          />
          <Box sx={{ display: 'flex', gap: 1.2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              sx={quietSx}
              onClick={() => navigator.clipboard.writeText(code).then(() => onCopied(true), () => onCopied(false))}
            >
              Copy
            </Button>
            <Button
              sx={primarySx}
              onClick={() => {
                if (backup) onDownloadBackup(backup);
                close();
              }}
            >
              Download file
            </Button>
          </Box>
        </Panel>
      </Dialog>

      <Dialog open={open === 'restore'} onClose={close} maxWidth="sm" fullWidth slotProps={{ paper: { sx: dialogPaper } }}>
        <Panel>
          <CloseButton onClose={close} />
          <DialogTitle>Restore a backup</DialogTitle>
          <Typography sx={{ fontSize: 14, opacity: 0.75, mb: 2.2, lineHeight: 1.5 }}>
            Paste the line you saved, or choose the file. Events already in Saved stay as they are;
            the rest are added.
          </Typography>
          <TextField
            value={pasted}
            onChange={(e) => {
              setPasted(e.target.value);
              setError(null);
            }}
            placeholder="cg1:…"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            slotProps={{ htmlInput: { 'aria-label': 'Backup code', spellCheck: false } }}
            sx={{ mb: error ? 1 : 2, ...fieldSx }}
          />
          {error && <Typography sx={{ fontSize: 13, color: '#ffb9b9', mb: 1.5, lineHeight: 1.5 }}>{error}</Typography>}
          <input ref={file} type="file" accept=".json,application/json,text/plain" hidden onChange={readFile} />
          <Box sx={{ display: 'flex', gap: 1.2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button sx={quietSx} onClick={() => file.current?.click()}>
              Choose file…
            </Button>
            <Button sx={primarySx} disabled={!pasted.trim()} onClick={() => restore(pasted)}>
              Restore
            </Button>
          </Box>
        </Panel>
      </Dialog>

      <Dialog open={open === 'clear'} onClose={close} maxWidth="xs" fullWidth slotProps={{ paper: { sx: dialogPaper } }}>
        <Panel>
          <DialogTitle>Clear Saved?</DialogTitle>
          <Typography sx={{ fontSize: 14, opacity: 0.75, mb: 2.4, lineHeight: 1.5 }}>
            This removes all {count} {count === 1 ? 'event' : 'events'} you marked Going or Maybe in {cityLabel}.
            It can't be undone, so back up first if you might want them again.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.2, justifyContent: 'flex-end' }}>
            <Button sx={quietSx} onClick={close} autoFocus>
              Cancel
            </Button>
            <Button
              sx={{ ...primarySx, background: '#ffb9b9', '&:hover': { background: '#ffd0d0' } }}
              onClick={() => {
                onClear();
                close();
              }}
            >
              Clear Saved
            </Button>
          </Box>
        </Panel>
      </Dialog>
    </>
  );
}

